// Translation hook - separated from provider for better Fast Refresh compatibility
import { useContext, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { TranslationContext } from '@/components/utils/translationHelpers';
import { loadLanguageNamespace } from '@/components/utils/translation/translationRegistry';

// Type for translation function
export type TranslationFunction = (
  page: string,
  context: string,
  fallback?: string | null,
  variables?: Record<string, unknown>
) => string;

// Type for translation context
interface TranslationContextType {
  t: TranslationFunction;
  currentLanguage: string;
  changeLanguage: (lang: string) => void;
  loading: boolean;
  forceUpdate: number;
  translations: Record<string, unknown>;
  fallbackTranslations: Record<string, unknown>;
  debugTranslations: () => void;
}

// Custom hook to use translations
export function useTranslation(): TranslationContextType {
  const context = useContext(TranslationContext);

  return context;
}

// Hook for lazy loading additional translation namespaces
export function useLazyTranslations(namespaces: string[]): void {
  const { currentLanguage } = useTranslation();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!namespaces.length || !currentLanguage) return;

    // Load additional namespaces for current language
    const loadNamespaces = async () => {
      try {
        for (const namespace of namespaces) {
          await loadLanguageNamespace(currentLanguage, namespace);

          // Invalidate translation queries to refresh the cache
          await queryClient.invalidateQueries({
            queryKey: ['translations', currentLanguage],
          });
        }
      } catch (error) {
        console.warn('Failed to lazy load translation namespaces:', error);
      }
    };

    void loadNamespaces();
  }, [namespaces, currentLanguage, queryClient]);
}
